import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer behavior.
 *
 * The <theme-drawer> element owns the real open/close lifecycle.
 * Cart line, discount, and note components own their AJAX + section rendering.
 *
 * This component:
 * - auto-opens the drawer after successful add-to-cart
 * - updates sticky summary state
 * - safely closes the drawer from the X button
 * - never replaces the drawer root
 * - never directly calls dialog.close()
 */

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (
      this.closest('dialog')
    );
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    /*
     * Delegated close handler.
     * This survives cart section morphing because the listener lives
     * on cart-drawer-component, not on the individual X button.
     */
    this.addEventListener(
      'click',
      this.#handleCloseClick,
      true
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * Restore/open path:
     * the drawer may already have [open] before this component finishes
     * connecting.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.removeEventListener(
      'click',
      this.#handleCloseClick,
      true
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Close the native cart drawer when the X button is clicked.
   *
   * IMPORTANT:
   * We use ThemeDrawer.close() instead of manipulating the dialog itself.
   * This preserves focus restoration, body scroll locking, animation,
   * drawer state and all native theme lifecycle events.
   */
  #handleCloseClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const closeButton = target.closest(
      '#cart-drawer .theme-drawer__close-button,' +
      '#cart-drawer [data-cart-drawer-close],' +
      '#cart-drawer [on\\:click="#cart-drawer/close"]'
    );

    if (!(closeButton instanceof HTMLElement)) return;

    /*
     * Let Shopify's declarative click handler execute normally.
     * If it does not, our microtask closes the drawer through ThemeDrawer.
     */
    queueMicrotask(() => {
      if (this.#themeDrawer?.isOpen) {
        this.#themeDrawer.close();
      }
    });
  };

  /**
   * Runs whenever the native ThemeDrawer opens.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
     * The payment-terms element can be re-created after cart morphing,
     * so find it again every time the drawer opens.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Handles successful cart line events for drawer auto-open.
   *
   * Cart line rendering itself remains owned by cart-items-component.
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    /*
     * When adding from Quick Add, wait for its modal to close first.
     * Otherwise the drawer can steal focus before the modal restores it.
     */
    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    /*
     * Open immediately for normal add-to-cart actions.
     */
    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
          });

        if (
          !shouldAutoOpen ||
          detail?.didError
        ) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        /*
         * Quick-add modal:
         * wait until the modal's own close lifecycle has completed.
         */
        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Cart update event rejected:',
            error
          );
        }
      });
  };

  /**
   * Returns whether the cart currently renders its empty state.
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  /**
   * Updates whether the cart summary should remain sticky.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    /*
     * Empty-cart state has no summary.
     */
    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight /
      Math.max(drawerHeight, 1);

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}